import { useCallback, useEffect, useState } from "react";

import { loadLocationForecasts } from "../data/weatherRepository";
import {
  bearingForField,
  coordinateKey,
  coordinatesForField,
  dingerConditions,
  findCurrentHour,
} from "../domain/weather";
import type { PlateRelativeWind, WeatherCoordinates } from "../domain/weather";

export const DINGER_RETRY_DELAY_MS = 60 * 1000;

export function emulatorDingerTimeOverride(
  useEmulators: string | undefined,
  forceDingerTime: string | undefined,
): boolean {
  return useEmulators === "true" && forceDingerTime === "true";
}

const forceDingerTime = emulatorDingerTimeOverride(
  import.meta.env.VITE_USE_EMULATORS,
  import.meta.env.VITE_FORCE_DINGER_TIME,
);

const emulatorConditions: PlateRelativeWind = {
  speedKmh: 22,
  directionDegrees: 25,
};

interface UseDingerTimeOptions {
  enabled: boolean;
  field: string | null;
  fetchImpl?: typeof fetch;
}

export function useDingerTime({
  enabled,
  field,
  fetchImpl,
}: UseDingerTimeOptions): {
  active: boolean;
  conditions: PlateRelativeWind | null;
  retry: () => void;
} {
  const [refreshToken, setRefreshToken] = useState(0);
  const [conditions, setConditions] = useState<PlateRelativeWind | null>(null);
  const retry = useCallback(() => setRefreshToken((token) => token + 1), []);

  useEffect(() => {
    if (!enabled) {
      setConditions(null);
      return undefined;
    }
    const location = coordinatesForField(field);
    const bearing = bearingForField(field);
    if (!location || bearing === null) {
      setConditions(null);
      return undefined;
    }
    if (forceDingerTime) {
      setConditions(emulatorConditions);
      return undefined;
    }

    const controller = new AbortController();
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const result = await loadLocationForecasts(
          [location as WeatherCoordinates],
          {
            signal: controller.signal,
            ...(fetchImpl ? { fetchImpl } : {}),
          },
        );
        if (controller.signal.aborted) return;
        const forecast = result.byCoordinate.get(coordinateKey(location));
        const hour = forecast ? findCurrentHour(forecast.hours) : null;
        setConditions(
          hour
            ? dingerConditions({
                windDirectionFrom: hour.windDirection,
                windSpeed: hour.windSpeed,
                bearing,
              })
            : null,
        );
        refreshTimer = setTimeout(
          () => setRefreshToken((token) => token + 1),
          Math.max(1000, result.expiresAt - Date.now()),
        );
      } catch (error) {
        if (controller.signal.aborted || errorName(error) === "AbortError")
          return;
        setConditions(null);
        refreshTimer = setTimeout(
          () => setRefreshToken((token) => token + 1),
          DINGER_RETRY_DELAY_MS,
        );
      }
    };
    void load();

    return () => {
      controller.abort();
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [enabled, field, refreshToken, fetchImpl]);

  return { active: conditions !== null, conditions, retry };
}

function errorName(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return null;
  }
  return typeof error.name === "string" ? error.name : null;
}
